import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
    cleanupUserDailyBonusEffects, deactivateUserDailyBonusEffect, getUserDailyBonus, grantUserDailyBonusEffect,
} from '../Api';
import UserDailyBonusAdminDto, {
    DailyBonusDayDto, DailyBonusEffectsChangeDto, UserDailyBonusEffectDto,
} from '../models/dtos/UserDailyBonusAdminDto';

/** Mirrors DailyBonusAdminService.MAX_GRANT_HOURS in betup-server-analytics. */
const MAX_GRANT_HOURS = 24 * 90;

const DURATION_PRESETS: { label: string; hours: number }[] = [
    { label: '1 h', hours: 1 },
    { label: '6 h', hours: 6 },
    { label: '1 day', hours: 24 },
    { label: '3 days', hours: 72 },
    { label: '7 days', hours: 168 },
    { label: '30 days', hours: 720 },
];

type PendingAction =
    | { kind: 'cleanup' }
    | { kind: 'deactivate'; effect: UserDailyBonusEffectDto }
    | null;

function formatDate(value?: string | number | null) {
    return value != null ? new Date(value).toLocaleString() : '—';
}

function formatMultiplier(value: number) {
    return 'x' + Number(value.toFixed(2)).toString();
}

/** "in 5 h", "in 2 d 3 h", or "passed" for a date in the past. */
function relativeFromNow(value: string | number) {
    const diffMs = new Date(value).getTime() - Date.now();
    if (diffMs <= 0) return 'passed';
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    if (hours >= 24) return `in ${Math.floor(hours / 24)} d ${hours % 24} h`;
    if (hours > 0) return `in ${hours} h ${minutes} min`;
    return `in ${Math.max(minutes, 1)} min`;
}

function dayChipColor(state: DailyBonusDayDto['state']): 'success' | 'primary' | 'default' {
    switch (state) {
        case 'TAKEN': return 'success';
        case 'AVAILABLE': return 'primary';
        default: return 'default';
    }
}

function streakSummary(data: UserDailyBonusAdminDto): { text: string; severity: 'info' | 'success' | 'warning' } {
    const { streak } = data;
    const total = data.days.length || 7;
    if (!streak.started) {
        return { text: 'The player has never opened the daily bonus. Day 1 is claimable as soon as they do.', severity: 'info' };
    }
    if (streak.streakBroken) {
        return {
            text: `Streak broken: ${streak.storedRewardsInRow} day(s) in a row were lost when the claim window closed. The next claim restarts at day 1.`,
            severity: 'warning',
        };
    }
    if (streak.availableNow) {
        return { text: `Day ${streak.todayNumber} of ${total} is claimable now.`, severity: 'success' };
    }
    return {
        text: `Today's bonus is claimed (${streak.rewardsInRow} in a row). Day ${streak.todayNumber} opens ${streak.nextRewardAfter != null ? relativeFromNow(streak.nextRewardAfter) : 'later'}.`,
        severity: 'info',
    };
}

export default function UserDailyBonusTab({ userId }: { userId: number }) {
    const [data, setData] = React.useState<UserDailyBonusAdminDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [grantOpen, setGrantOpen] = React.useState(false);
    const [grantEffectId, setGrantEffectId] = React.useState<number | ''>('');
    const [grantHours, setGrantHours] = React.useState('24');

    const load = React.useCallback(async () => {
        setLoading(true);
        const result = await getUserDailyBonus(userId);
        setLoading(false);
        if (result == null) {
            setError('Failed to load the daily bonus state.');
            return;
        }
        setError(null);
        setData(result);
    }, [userId]);

    React.useEffect(() => { load(); }, [load]);

    const activeEffects = React.useMemo(() => data?.effects.filter(e => e.isActive) ?? [], [data]);

    const applyResult = (
        result: { data: DailyBonusEffectsChangeDto | null; error: string | null },
        failure: string,
        success: (change: DailyBonusEffectsChangeDto) => string,
    ) => {
        if (result.data == null) {
            setError(`${failure}: ${result.error ?? 'unknown error'}`);
            return false;
        }
        setError(null);
        setData(result.data.state);
        setNotice(success(result.data));
        return true;
    };

    const runPending = async () => {
        if (pending == null) return;
        const action = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        if (action.kind === 'cleanup') {
            const result = await cleanupUserDailyBonusEffects(userId);
            applyResult(result, 'Cleanup failed', change =>
                `Deactivated ${change.changed} daily bonus effect(s); the multiplier is now ${formatMultiplier(change.state.multiplier)}.`);
        } else {
            const result = await deactivateUserDailyBonusEffect(userId, action.effect.userEffectId);
            applyResult(result, `Failed to deactivate "${action.effect.name ?? action.effect.effectId}"`, change =>
                `Deactivated "${action.effect.name ?? action.effect.effectId}"; the multiplier is now ${formatMultiplier(change.state.multiplier)}.`);
        }
        setBusy(false);
    };

    const parsedHours = Number(grantHours);
    const hoursValid = Number.isInteger(parsedHours) && parsedHours >= 1 && parsedHours <= MAX_GRANT_HOURS;
    const selectedEffect = data?.availableEffects.find(e => e.effectId === grantEffectId) ?? null;
    const multiplierAfterGrant = selectedEffect != null && data != null
        ? Math.max(selectedEffect.coef, activeEffects.length > 0 ? data.multiplier : 0)
        : null;

    const openGrant = () => {
        const options = data?.availableEffects ?? [];
        setGrantEffectId(options.length > 0 ? options[options.length - 1].effectId : '');
        setGrantHours('24');
        setGrantOpen(true);
    };

    const runGrant = async () => {
        if (grantEffectId === '' || !hoursValid) return;
        setGrantOpen(false);
        setBusy(true);
        setNotice(null);
        const result = await grantUserDailyBonusEffect(userId, grantEffectId, parsedHours);
        applyResult(result, 'Failed to add the effect', change =>
            `Added "${selectedEffect?.name ?? grantEffectId}" for ${parsedHours} h; the multiplier is now ${formatMultiplier(change.state.multiplier)}.`);
        setBusy(false);
    };

    if (loading && data == null) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>;
    }

    const summary = data ? streakSummary(data) : null;

    return (
        <Stack gap={2} sx={{ py: 2 }}>
            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                {data && (
                    <Tooltip title="Highest coefficient among the player's active daily bonus effects (group 15); x1 without any. Applied to every daily bonus claim, including the video-doubled one.">
                        <Chip
                            color={data.multiplier > 1 ? 'success' : 'default'}
                            label={`Multiplier ${formatMultiplier(data.multiplier)}`}
                            sx={{ fontWeight: 600, fontSize: '1rem', height: 36, px: 1 }}
                        />
                    </Tooltip>
                )}
                <Button variant="contained" onClick={openGrant} disabled={busy || data == null || data.availableEffects.length === 0}>
                    Add temporary effect
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setPending({ kind: 'cleanup' })}
                    disabled={busy || activeEffects.length === 0}
                >
                    Clean up effects ({activeEffects.length})
                </Button>
                <Button variant="outlined" onClick={load} disabled={busy || loading}>Refresh</Button>
                {busy && <CircularProgress size={22} />}
            </Stack>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

            {data && summary && (
                <React.Fragment>
                    <Alert severity={summary.severity}>{summary.text}</Alert>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Streak</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>In a row</TableCell>
                                        <TableCell>Last claim</TableCell>
                                        <TableCell>Next claim opens</TableCell>
                                        <TableCell>Streak breaks at</TableCell>
                                        <TableCell>Timezone</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            {data.streak.rewardsInRow}
                                            {data.streak.streakBroken && (
                                                <Typography component="span" variant="body2" color="text.secondary"> (stored {data.streak.storedRewardsInRow})</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(data.streak.lastDayAcquired)}</TableCell>
                                        <TableCell>
                                            {formatDate(data.streak.nextRewardAfter)}
                                            {data.streak.nextRewardAfter != null && !data.streak.availableNow && (
                                                <Typography variant="caption" display="block" color="text.secondary">{relativeFromNow(data.streak.nextRewardAfter)}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(data.streak.nextRewardBefore)}
                                            {data.streak.nextRewardBefore != null && !data.streak.streakBroken && (
                                                <Typography variant="caption" display="block" color="text.secondary">{relativeFromNow(data.streak.nextRewardBefore)}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{data.timezone}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1.5 }}>
                        {data.days.map(day => (
                            <Card
                                key={day.dayNumber}
                                variant="outlined"
                                sx={{
                                    borderColor: day.state === 'AVAILABLE' ? 'primary.main' : undefined,
                                    borderWidth: day.state === 'AVAILABLE' ? 2 : 1,
                                    opacity: day.state === 'LOCKED' ? 0.75 : 1,
                                }}
                            >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack alignItems="center" gap={0.5}>
                                        <Typography variant="subtitle2">Day {day.dayNumber}</Typography>
                                        {day.photoUrl && <img src={day.photoUrl} alt="" width={40} height={40} style={{ objectFit: 'contain' }} />}
                                        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{day.betcoins.toLocaleString()}</Typography>
                                        {data.multiplier !== 1 && (
                                            <Typography variant="caption" color="text.secondary">
                                                {day.baseBetcoins.toLocaleString()} {formatMultiplier(data.multiplier)}
                                            </Typography>
                                        )}
                                        {day.tickets > 0 && <Typography variant="caption" color="text.secondary">{day.tickets} ticket(s)</Typography>}
                                        <Chip size="small" color={dayChipColor(day.state)} label={day.state.toLowerCase()} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6">Daily bonus effects</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                The highest active coefficient wins. Expired effects are turned off by the Crons job within a minute.
                                Cleaned-up level rewards are granted again on the player's next level-up. While the multiplier is above x1, the iOS shop hides daily bonus boosters.
                            </Typography>
                            {data.effects.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">The player has never had a daily bonus effect.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Effect</TableCell>
                                            <TableCell>Coef</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell>Applied</TableCell>
                                            <TableCell>Expires</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.effects.map(effect => (
                                            <TableRow key={effect.userEffectId} sx={{ opacity: effect.isActive ? 1 : 0.55 }}>
                                                <TableCell>
                                                    {effect.name ?? `Effect ${effect.effectId}`}
                                                    <Typography variant="caption" display="block" color="text.secondary">#{effect.effectId} · row {effect.userEffectId}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: effect.isActive && effect.coef === data.multiplier ? 700 : undefined }}>
                                                    {formatMultiplier(effect.coef)}
                                                </TableCell>
                                                <TableCell>{effect.causeLabel}</TableCell>
                                                <TableCell>{formatDate(effect.applied)}</TableCell>
                                                <TableCell>
                                                    {effect.expires == null ? 'Never' : formatDate(effect.expires)}
                                                    {effect.expires != null && effect.isActive && !effect.pendingExpiry && (
                                                        <Typography variant="caption" display="block" color="text.secondary">{relativeFromNow(effect.expires)}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {effect.pendingExpiry
                                                        ? <Tooltip title="Past its expiry; still counted until the Crons job turns it off."><Chip size="small" color="warning" label="Expiring" /></Tooltip>
                                                        : <Chip size="small" color={effect.isActive ? 'success' : 'default'} label={effect.isActive ? 'Active' : 'Inactive'} />}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {effect.isActive && (
                                                        <Button size="small" color="error" disabled={busy} onClick={() => setPending({ kind: 'deactivate', effect })}>
                                                            Deactivate
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Recent claims</Typography>
                            {data.recentClaims.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No daily bonus claims in the balance history.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When</TableCell>
                                            <TableCell align="right">Betcoins</TableCell>
                                            <TableCell align="right">Balance after</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.recentClaims.map((claim, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{formatDate(claim.timestamp)}</TableCell>
                                                <TableCell align="right">+{claim.amount.toLocaleString()}</TableCell>
                                                <TableCell align="right">{claim.newBalance.toLocaleString()}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </React.Fragment>
            )}

            <Dialog open={pending != null} onClose={() => setPending(null)}>
                <DialogTitle>{pending?.kind === 'cleanup' ? 'Clean up daily bonus effects?' : 'Deactivate this effect?'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pending?.kind === 'cleanup'
                            ? `All ${activeEffects.length} active daily bonus effect(s) will be turned off, so the player's daily bonus goes back to x1. Purchased boosters are not refunded.`
                            : pending?.kind === 'deactivate'
                                ? `"${pending.effect.name ?? pending.effect.effectId}" (${formatMultiplier(pending.effect.coef)}, ${pending.effect.causeLabel.toLowerCase()}) will stop counting right away.`
                                : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={runPending}>
                        {pending?.kind === 'cleanup' ? 'Clean up' : 'Deactivate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add a temporary daily bonus effect</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ pt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="daily-bonus-effect-label">Effect</InputLabel>
                            <Select
                                labelId="daily-bonus-effect-label"
                                label="Effect"
                                value={grantEffectId}
                                onChange={e => setGrantEffectId(e.target.value === '' ? '' : Number(e.target.value))}
                            >
                                {(data?.availableEffects ?? []).map(option => (
                                    <MenuItem key={option.effectId} value={option.effectId}>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Chip size="small" label={formatMultiplier(option.coef)} />
                                            <span>{option.name ?? `Effect ${option.effectId}`}</span>
                                            <Typography variant="caption" color="text.secondary">
                                                #{option.effectId}{option.level != null ? ` · level ${option.level} reward` : ''}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {selectedEffect?.description && (
                            <Typography variant="body2" color="text.secondary">{selectedEffect.description}</Typography>
                        )}
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {DURATION_PRESETS.map(preset => (
                                <Chip
                                    key={preset.hours}
                                    label={preset.label}
                                    color={parsedHours === preset.hours ? 'primary' : 'default'}
                                    onClick={() => setGrantHours(String(preset.hours))}
                                />
                            ))}
                        </Stack>
                        <TextField
                            label="Duration (hours)"
                            value={grantHours}
                            inputMode="numeric"
                            onChange={e => setGrantHours(e.target.value.replace(/[^\d]/g, ''))}
                            error={!hoursValid}
                            helperText={hoursValid
                                ? `Expires around ${new Date(Date.now() + parsedHours * 3_600_000).toLocaleString()}`
                                : `Whole hours from 1 to ${MAX_GRANT_HOURS}`}
                        />
                        {selectedEffect != null && multiplierAfterGrant != null && data != null && (
                            selectedEffect.coef <= data.multiplier && activeEffects.length > 0
                                ? <Alert severity="warning">
                                    The player already has {formatMultiplier(data.multiplier)}, so this effect only counts once the higher one ends or is cleaned up.
                                </Alert>
                                : <Alert severity="info">
                                    The multiplier goes from {formatMultiplier(data.multiplier)} to {formatMultiplier(multiplierAfterGrant)} for the next claims.
                                </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={runGrant} disabled={grantEffectId === '' || !hoursValid}>Add effect</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
